<template>
    <div class="ai-call-attribute-picker">
        <div class="filter-menu">
            <SupsisButton v-if="selectedMenu.length" ghost small @click="prev">
                <span class="material-icons"> chevron_left </span>
            </SupsisButton>
            <div class="breadcrumb-item" v-for="menu in selectedMenu" :key="menu">
                <span> {{ findAttributeByValue(menu).label }} ✦ </span>
            </div>
        </div>

        <div class="webhook-blocks" v-if="!selectedMenu.length">
            <div class="block-item" v-for="block in aiCallBlocks" :key="block.id" @click="selectBlock(block)">
                <span class="material-icons voice-agent-icon">record_voice_over</span>
                <span class="name">{{ block.name || "AI Call" }}</span>
            </div>
        </div>

        <div class="attribute-list" v-else>
            <div class="attr-select">
                <div
                    class="picker-item"
                    v-for="attr in filterAttributes()"
                    @click="() => select(attr.value)"
                    :key="attr.value"
                >
                    <span class="name">{{ attr.label }}</span>
                    <span class="description" v-if="attr.description">{{ attr.description }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";
import flatDiagram from "../../../utils/flatDiagram";

export default {
    name: "AICallAttributePicker",
    components: {
        SupsisButton,
    },
    props: {
        onPicked: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            searchText: "",
            attributes: [],
            selectedMenu: [],
            aiCallBlocks: [],
            selectedBlock: null,
        };
    },
    computed: {
        ...mapGetters({
            board: "CB/board",
        }),
    },
    methods: {
        listAICallBlocks() {
            const storyId = this.$route.params.id;
            const board = this.board(storyId);
            const flatted = flatDiagram(board.diagram);
            return flatted.filter((block) => block.type === "AI_CALL");
        },

        generateAttributesForBlock(block) {
            const blockId = block.id;
            const basePrefix = `block.${blockId}`;

            const attributes = [
                {
                    label: "*",
                    value: `${basePrefix}`,
                    description: "*",
                },
                {
                    label: "Success",
                    value: `${basePrefix}.success`,
                    description: "Arama başarılı oldu mu?",
                },
                {
                    label: "Error",
                    value: `${basePrefix}.error`,
                    description: "Hata mesajı (varsa)",
                },
                {
                    label: "Call",
                    value: `${basePrefix}.call`,
                    description: "Arama bilgileri",
                    subAttributes: this.generateCallAttributes(`${basePrefix}.call`)
                }
            ];

            return attributes;
        },

        generateCallAttributes(prefix) {
            return [
                {
                    label: "*",
                    value: `${prefix}.*`,
                    description: "*",
                },
                {
                    label: "call_id",
                    value: `${prefix}.call_id`,
                    description: "Arama kimliği",
                },
                {
                    label: "call_type",
                    value: `${prefix}.call_type`,
                    description: "Arama tipi",
                },
                {
                    label: "call_status",
                    value: `${prefix}.call_status`,
                    description: "Aramadaki son durum",
                },
                {
                    label: "agent_id",
                    value: `${prefix}.agent_id`,
                    description: "Ajan kimliği",
                },
                {
                    label: "from_number",
                    value: `${prefix}.from_number`,
                    description: "Arayan numara",
                },
                {
                    label: "to_number",
                    value: `${prefix}.to_number`,
                    description: "Aranan numara",
                },
                {
                    label: "start_timestamp",
                    value: `${prefix}.start_timestamp`,
                    description: "Aramanın başlangıç zamanı (timestamp)",
                },
                {
                    label: "end_timestamp",
                    value: `${prefix}.end_timestamp`,
                    description: "Aramanın bitiş zamanı (timestamp)",
                },
                {
                    label: "duration_ms",
                    value: `${prefix}.duration_ms`,
                    description: "Arama süresi (milisaniye)",
                },
                {
                    label: "duration",
                    value: `${prefix}.duration`,
                    description: "Arama süresi (saniye)",
                },
                {
                    label: "recording_url",
                    value: `${prefix}.recording_url`,
                    description: "Kayıt URL'i",
                },
                {
                    label: "transcript",
                    value: `${prefix}.transcript`,
                    description: "Görüşme transkripti (düz metin)",
                },
                {
                    label: "transcript_object",
                    value: `${prefix}.transcript_object`,
                    description: "Görüşme transkripti (detaylı)",
                },
                {
                    label: "transcript_with_tool_calls",
                    value: `${prefix}.transcript_with_tool_calls`,
                    description: "Görüşme transkripti (araç çağrıları ile)",
                },
                {
                    label: "disconnection_reason",
                    value: `${prefix}.disconnection_reason`,
                    description: "Bağlantı kesme nedeni",
                },
                {
                    label: "public_log_url",
                    value: `${prefix}.public_log_url`,
                    description: "Kamuya açık log URL'i",
                },
                {
                    label: "latency",
                    value: `${prefix}.latency`,
                    description: "Gecikme metrikleri",
                },
                {
                    label: "direction",
                    value: `${prefix}.direction`,
                    description: "Aramanın yönü (gelen/giden)",
                },
                {
                    label: "summary",
                    value: `${prefix}.summary`,
                    description: "Görüşme özeti",
                }
            ];
        },

        selectBlock(block) {
            this.selectedBlock = block;
            this.attributes = this.generateAttributesForBlock(block);
            this.selectedMenu.push("root");
        },

        filterAttributes() {
            const parentAttribute = this.getParentAttribute();
            let attrs = parentAttribute ? parentAttribute.subAttributes : this.attributes;

            return attrs.filter((attr) => {
                if (this.searchText) {
                    return (attr.label || "").toLowerCase().includes(this.searchText.toLowerCase());
                }
                return true;
            });
        },

        getParentAttribute() {
            if (this.selectedMenu.length <= 1) return null;
            const attributes = this.getFlatAttributes();
            const parent = this.selectedMenu[this.selectedMenu.length - 1];
            return attributes.find((x) => x.value === parent);
        },

        getFlatAttributes() {
            const flat = (attr) => {
                if (attr.subAttributes) {
                    return [attr, ...attr.subAttributes.flatMap(flat)];
                }
                return [attr];
            };
            return this.attributes.flatMap(flat);
        },

        select(value) {
            const attributes = this.getFlatAttributes();
            const attr = attributes.find((x) => x.value === value);
            if (Array.isArray(attr.subAttributes) && attr.subAttributes.length) {
                this.selectedMenu.push(value);
            } else {
                this.onPicked(value);
            }
        },

        prev() {
            if (this.selectedMenu.length === 1) {
                this.selectedMenu = [];
                this.selectedBlock = null;
            } else {
                this.selectedMenu.pop();
            }
        },

        findAttributeByValue(value) {
            if (value === "root") {
                return { label: this.selectedBlock?.name || "AI Call" };
            }
            const attributes = this.getFlatAttributes();
            return attributes.find((x) => x.value === value);
        },
    },
    created() {
        this.aiCallBlocks = this.listAICallBlocks();
    },
};
</script>

<style lang="scss" scoped>
.ai-call-attribute-picker {
    background: #fff;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    max-height: 600px;
    min-height: 200px;
    user-select: none;
    width: 100%;
    padding: 5px;

    .filter-menu {
        display: flex;
        align-items: center;
        gap: 3px;
        margin-bottom: 10px;

        .supsis-button {
            border: none !important;
            font-size: 13px;
            height: 25px;
        }

        .breadcrumb-item {
            display: flex;
        }
    }

    .webhook-blocks {
        .block-item {
            display: flex;
            align-items: center;
            padding: 8px;
            cursor: pointer;
            border-radius: 4px;
            margin-bottom: 4px;

            &:hover {
                background-color: #f0f4f7;
            }

            .material-icons {
                margin-right: 8px;
                color: #647283;
            }

            .voice-agent-icon {
                margin-right: 8px;
                color: #647283;
            }

            .name {
                color: #22272d;
                font-size: 14px;
            }
        }
    }

    .attribute-list {
        flex-grow: 1;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 5px;
        margin: 0px 5px 0px 5px;

        .picker-item {
            align-items: center;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            padding: 8px;
            transition: background-color 0.2s;
            border-radius: 4px;

            &:hover {
                background-color: #f0f4f7;
            }

            .name {
                color: #22272d;
                font-size: 14px;
            }

            .description {
                color: #9ba6b3;
                font-size: 12px;
            }
        }
    }
}
</style> 